import { Link } from 'react-router-dom';
import JsonLd from '../components/JsonLd';
import { GUIDE_BY_SLUG, howToSchema, faqSchema } from '../content/guides';

/**
 * One component, every task page — the content lives in src/content/guides.js
 * so the page and the prerenderer read the same source and cannot drift.
 *
 * The CTA is the live product rather than a link to it: someone who searched
 * "how to send a cbct scan" wants to send one, and the form is one click away
 * at the top of this very site.
 */
// slug comes in as a prop: the routes are enumerated per guide with no :slug
// segment, so useParams() would be empty here and every page would render the
// not-found branch.
export default function Guide({ slug }) {
  const g = GUIDE_BY_SLUG[slug];

  // Unknown slug: the SPA catch-all already renders the upload page, but a
  // direct hit here should still land somewhere useful rather than blank.
  if (!g) {
    return (
      <div className="page narrow">
        <div className="card">
          <h1>Page not found</h1>
          <Link className="btn btn-primary" to="/">Send a file</Link>
        </div>
      </div>
    );
  }

  const schema = howToSchema(g);
  const faq = faqSchema(g);

  return (
    <div className="landing">
      <JsonLd id={`howto-${g.slug}`} data={schema} />
      <JsonLd id={`faq-${g.slug}`} data={faq} />

      <section className="hero">
        <div className="eyebrow">Guide</div>
        <h1>{g.h1}</h1>
        <p className="hero-lead">{g.lede}</p>
        <div style={{ marginTop: 20 }}>
          <Link className="btn btn-primary" to="/">Send a file now — free</Link>
        </div>
      </section>

      <section className="section">
        <h2 className="section-title">Step by step</h2>
        <div className="step-list">
          {g.steps.map((s, i) => (
            <div key={s.name} className="detail-step">
              <div className="detail-step-num">{i + 1}</div>
              <div className="detail-step-body">
                <h3>{s.name}</h3>
                <p>{s.text}</p>
              </div>
            </div>
          ))}
        </div>

        {g.related && (
          <p style={{ marginTop: 18 }}>
            {g.related.internal
              ? <Link to={g.related.href}>{g.related.label}</Link>
              : <a href={g.related.href}>{g.related.label}</a>}
          </p>
        )}

        <div className="callout" style={{ marginTop: 24 }}>
          <strong>Free for dental offices.</strong> Five secure sends a month with no
          credit card; unlimited sending is included with Practice Autopilot and AI
          Dental Office, or
          $19.99/year for an individual sender.
        </div>

        <h2 className="section-title" style={{ marginTop: 36 }}>Common questions</h2>
        <div className="faq-list">
          {/* <details>, not a JS accordion: these pages are prerendered, so they look
              interactive well before the bundle attaches handlers, and clicks in that
              window did nothing. It also puts every answer in the HTML — which is what
              the FAQPage schema promises Google is on the page. */}
          {g.faqs.map((f, i) => (
            <details key={f.q} className="faq-item" open={i === 0}>
              <summary className="faq-q">
                <span>{f.q}</span>
                <span className="faq-chev" aria-hidden>+</span>
              </summary>
              <div className="faq-a"><p>{f.a}</p></div>
            </details>
          ))}
        </div>

        <div style={{ textAlign: 'center', marginTop: 32 }}>
          <Link className="btn btn-primary" to="/">Send a file now — free</Link>
        </div>
      </section>
    </div>
  );
}
